/**
 * timeEntriesApi: the transport and react-query hooks behind My time and
 * the internal-time card (the clientRecordApi.ts pattern).
 *
 *   GET    /api/t/{slug}/v1/my-hours?period_start=  -> MyHoursResponse
 *   GET    /api/t/{slug}/v1/time-entries?from&to    -> { entries }
 *   POST   /api/t/{slug}/v1/time-entries            -> { entry }
 *   PATCH  /api/t/{slug}/v1/time-entries/{id}       -> { entry }
 *   DELETE /api/t/{slug}/v1/time-entries/{id}       -> { deleted, id }
 *
 * Hours cross the wire as strings; My time and the internal-time card
 * show them through formatHoursTenths (src/lib/hours.ts), and nothing in
 * this file parses them. A write invalidates both reads, so the summary
 * and the entry list move together.
 *
 * invalidateMyHours is the one call every site that changes hours makes:
 * the mutations in this file, and the note-writing sites elsewhere
 * (NoteComposer, NotesFeed, PreBilling) that change a staff_hours row
 * without going through this file's own mutations. The SPICE doors post
 * through NoteComposer.
 */

import { writeFetch } from './writeFetch';
import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';
import { errorCodeOf, fieldOf, pageWriteText, serverSentence, type SupportContact } from './errorCopy';
import { FIELD_ERROR_FALLBACK, TIME_ENTRY_FIELDS, fieldSentence } from './fieldErrorCopy';
import type { TimeEntryKind } from './timeEntryKinds';

export { TIME_ENTRY_KINDS, TIME_ENTRY_KIND_LABEL, type TimeEntryKind } from './timeEntryKinds';

export interface HoursPeriodWire {
  start: string;
  end: string;
  label: string;
  open: boolean;
  editable_until: string | null;
}

export interface StaffHoursRowWire {
  staff_user_id: string;
  staff_display_name: string;
  roster_id: string | null;
  employment_type: string | null;
  billable_role: string | null;
  work_date: string;
  hours: string;
  source: 'case_note' | 'internal';
  kind: string;
  client_id: string | null;
  client_display_name: string | null;
  rate_band: string | null;
  after_hours: boolean;
  billing_ready: boolean;
  ref: string;
  /** True on a note written on a demo client; false on every other note
   *  and on every internal row. */
  client_is_demo: boolean;
}

export interface HoursByBandWire {
  rate_band: string | null;
  hours: string;
}

export interface HoursByClientWire {
  client_id: string;
  client_display_name: string;
  hours: string;
  bands: HoursByBandWire[];
}

export interface PersonHoursSummaryWire {
  staff_user_id: string;
  staff_display_name: string;
  roster_id: string | null;
  employment_type: string | null;
  billable_role: string | null;
  clients: HoursByClientWire[];
  bands: HoursByBandWire[];
  after_hours: string;
  internal: Array<{ kind: string; hours: string }>;
  client_hours: string;
  internal_hours: string;
  total_hours: string;
}

export interface MyHoursResponse {
  period: HoursPeriodWire;
  config_present: boolean;
  rows: StaffHoursRowWire[];
  summary: PersonHoursSummaryWire;
}

export interface TimeEntryWire {
  id: string;
  kind: TimeEntryKind;
  hours: string;
  occurred_on: string;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export interface TimeEntryInput {
  kind: TimeEntryKind;
  /** A JSON number on the wire; the server normalizes to numeric(6,2). */
  hours: number;
  occurred_on: string;
  note: string | null;
}

function tenantUrl(slug: string, path: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1${path}`;
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  slug: string,
  path: string,
  body?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const url = tenantUrl(slug, path);
  const res = await writeFetch(url, {
    method,
    headers: body === undefined
      ? { Accept: 'application/json' }
      : { Accept: 'application/json', 'Content-Type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    redirect: 'manual',
    signal,
  }, { redirectToSignIn: true });
  if (!res.ok) {
    let parsed: unknown;
    try {
      parsed = await res.json();
    } catch {
      parsed = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, parsed, `${method} ${path} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export const timeEntriesApi = {
  myHours(slug: string, periodStart: string | null, signal?: AbortSignal): Promise<MyHoursResponse> {
    const q = periodStart === null ? '' : `?period_start=${encodeURIComponent(periodStart)}`;
    return request<MyHoursResponse>('GET', slug, `/my-hours${q}`, undefined, signal);
  },
  list(slug: string, from: string, to: string, signal?: AbortSignal): Promise<{ entries: TimeEntryWire[] }> {
    return request('GET', slug, `/time-entries?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, undefined, signal);
  },
  create(slug: string, input: TimeEntryInput): Promise<{ entry: TimeEntryWire }> {
    return request('POST', slug, '/time-entries', { ...input });
  },
  update(slug: string, id: string, patch: Partial<TimeEntryInput>): Promise<{ entry: TimeEntryWire }> {
    return request('PATCH', slug, `/time-entries/${encodeURIComponent(id)}`, { ...patch });
  },
  remove(slug: string, id: string): Promise<{ deleted: boolean; id: string }> {
    return request('DELETE', slug, `/time-entries/${encodeURIComponent(id)}`);
  },
};

export function myHoursKey(slug: string, periodStart: string | null): readonly unknown[] {
  return ['my-hours', slug, periodStart] as const;
}

/**
 * Invalidate every my-hours query cached for this tenant, one period or
 * many. The prefix ['my-hours', slug] matches every period's full key
 * under TanStack's default partial match, so this reaches My time
 * regardless of which period it is showing.
 */
export function invalidateMyHours(queryClient: QueryClient, slug: string): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: ['my-hours', slug] });
}

export function timeEntriesKey(slug: string, from: string, to: string): readonly unknown[] {
  return ['time-entries', slug, from, to] as const;
}

export interface UseMyHoursResult {
  data: MyHoursResponse | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** How many times the read has failed; a caller tells a new failure
   *  from one it has already seen (My Clients' + Internal Time). */
  errorUpdateCount: number;
  refetch: () => void;
}

/**
 * One person's period: the ledger rows and their summary. The previous
 * period stays on screen while the next one loads, so the page header
 * (and the focus on the control that asked) survives a period change.
 */
export function useMyHours(
  slug: string,
  periodStart: string | null,
  opts: { enabled?: boolean } = {},
): UseMyHoursResult {
  const query = useQuery<MyHoursResponse>({
    queryKey: myHoursKey(slug, periodStart),
    queryFn: ({ signal }) => timeEntriesApi.myHours(slug, periodStart, signal),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    enabled: (opts.enabled ?? true) && slug !== '',
  });
  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    errorUpdateCount: query.errorUpdateCount,
    refetch: () => {
      void query.refetch();
    },
  };
}

/** The caller's own internal entries dated in [from, to]. */
export function useTimeEntries(
  slug: string,
  from: string,
  to: string,
  opts: { enabled?: boolean } = {},
): { entries: TimeEntryWire[]; isLoading: boolean; isError: boolean } {
  const query = useQuery({
    queryKey: timeEntriesKey(slug, from, to),
    queryFn: ({ signal }) => timeEntriesApi.list(slug, from, to, signal),
    staleTime: 30_000,
    enabled: (opts.enabled ?? true) && slug !== '',
  });
  return { entries: query.data?.entries ?? [], isLoading: query.isLoading, isError: query.isError };
}

function useInvalidateTime(slug: string): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([invalidateMyHours(qc, slug), qc.invalidateQueries({ queryKey: ['time-entries', slug] })]);
  };
}

export function useCreateTimeEntry(slug: string) {
  const invalidate = useInvalidateTime(slug);
  return useMutation({
    mutationFn: (input: TimeEntryInput) => timeEntriesApi.create(slug, input),
    onSuccess: invalidate,
  });
}

export function useUpdateTimeEntry(slug: string) {
  const invalidate = useInvalidateTime(slug);
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<TimeEntryInput> }) => timeEntriesApi.update(slug, id, patch),
    onSuccess: invalidate,
  });
}

export function useDeleteTimeEntry(slug: string) {
  const invalidate = useInvalidateTime(slug);
  return useMutation({
    mutationFn: (id: string) => timeEntriesApi.remove(slug, id),
    onSuccess: invalidate,
  });
}

/** The on-screen line for a refused write. The server's own sentence for
 *  the two codes whose copy it authors; a field the card has, in one plain
 *  sentence chosen by the value the card sent (`sent`, undefined for a
 *  remove); otherwise the house sentence. The validator's words are never
 *  read. */
export function timeEntryErrorMessage(
  err: unknown,
  contact: SupportContact,
  sent?: Readonly<Partial<Record<keyof TimeEntryInput, unknown>>>,
): string {
  const server = serverSentence(err, 'time_entries');
  if (server !== null) return server;
  if (errorCodeOf(err) === 'pay_period_closed') return 'That pay period has gone to payroll.';
  if (err instanceof ApiError && err.status === 400) {
    const f = fieldOf(err);
    const field = f !== null && Object.prototype.hasOwnProperty.call(TIME_ENTRY_FIELDS, f) ? TIME_ENTRY_FIELDS[f] : undefined;
    if (field === undefined) return FIELD_ERROR_FALLBACK;
    return fieldSentence(field.label, field.rule, sent?.[f as keyof TimeEntryInput]);
  }
  return pageWriteText(err, contact);
}
